import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="bg-gray-50 text-gray-800 px-4 py-12 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-md">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Privacy Policy &amp; Terms of Use (EULA)
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Last updated: August 24, 2026
        </p>

        <p className="mb-4">
          This page contains the{" "}
          <strong>Privacy Policy</strong> and{" "}
          <strong>Terms of Use (End User License Agreement / EULA)</strong> for{" "}
          <strong>It&apos;s All About The Options</strong> (&quot;IAATO,&quot;
          &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;), including our
          website and mobile applications. By accessing or using our Services,
          you agree to these terms. If you do not agree, do not use the Services.
        </p>

        <p className="mb-4">
          Website:{" "}
          <a
            href="https://itsallaboutheoptions.com"
            className="text-indigo-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://itsallaboutheoptions.com
          </a>
        </p>

        {/* ========== PRIVACY POLICY ========== */}
        <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2">
          Privacy Policy
        </h2>

        <p className="mb-4">
          We are committed to protecting your privacy. This Privacy Policy
          explains how we collect, use, disclose, and safeguard your information
          when you visit our website or use our apps.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          Information We Collect
        </h3>
        <p className="mb-2">
          We may collect personal information that you voluntarily provide to us
          when you:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>Subscribe to our newsletter</li>
          <li>Register on the site or in the app</li>
          <li>Purchase or manage a subscription</li>
          <li>Participate in surveys or promotions</li>
          <li>Contact us for support or inquiries</li>
        </ul>

        <p className="mb-4">The personal information we collect may include:</p>
        <ul className="list-disc list-inside mb-4">
          <li>Name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Account and subscription details</li>
          <li>Any other information you choose to provide</li>
        </ul>

        <p className="mb-4">We may also collect non-personal information such as:</p>
        <ul className="list-disc list-inside mb-4">
          <li>Browser type</li>
          <li>Operating system</li>
          <li>IP address</li>
          <li>Device identifiers</li>
          <li>Pages visited and time spent on the site or app</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          How We Use Your Information
        </h3>
        <ul className="list-disc list-inside mb-4">
          <li>Provide and maintain our services</li>
          <li>Process subscriptions and payments</li>
          <li>Improve user experience</li>
          <li>Send newsletters and promotional materials</li>
          <li>Respond to inquiries and provide support</li>
          <li>Monitor and analyze usage patterns</li>
        </ul>

        <p className="mb-4">
          We do not sell, trade, or rent your personal information to others. We
          may share information with third-party service providers who assist us
          in operating our website, apps, and business, provided they agree to
          keep this information confidential. Purchases made through the Apple
          App Store are also subject to Apple&apos;s privacy practices.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          Cookies and Tracking
        </h3>
        <p className="mb-4">
          Our website may use cookies and similar tracking technologies to
          enhance user experience. You can choose to set your web browser to
          refuse cookies or to alert you when cookies are being sent.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Data Security</h3>
        <p className="mb-4">
          We implement appropriate security measures to protect against
          unauthorized access, alteration, disclosure, or destruction of your
          personal information.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Your Rights</h3>
        <p className="mb-2">
          Depending on your location, you may have rights under data protection
          laws, including:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>The right to access the personal data we hold about you</li>
          <li>
            The right to request correction or deletion of your personal data
          </li>
          <li>
            The right to object to or restrict certain processing of your data
          </li>
          <li>The right to data portability</li>
        </ul>
        <p className="mb-4">
          To exercise these rights, please contact us at{" "}
          <strong>contact@itsallaboutheoptions.com</strong>.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Third-Party Links</h3>
        <p className="mb-4">
          Our website and apps may contain links to third-party websites. We are
          not responsible for the privacy practices of these external sites.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Policy Updates</h3>
        <p className="mb-4">
          We may update this Privacy Policy from time to time. We encourage you
          to review this policy periodically for any changes. Continued use of
          the Services after changes are posted constitutes acceptance of the
          updated policy.
        </p>

        {/* ========== TERMS OF USE / EULA ========== */}
        <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2">
          Terms of Use (EULA)
        </h2>

        <p className="mb-4">
          This End User License Agreement (&quot;EULA&quot; or &quot;Terms of
          Use&quot;) governs your use of the It&apos;s All About The Options
          mobile application(s) and related Services. By downloading,
          installing, or using the app, you agree to this EULA.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">1. License Grant</h3>
        <p className="mb-4">
          Subject to your compliance with this EULA, we grant you a limited,
          non-exclusive, non-transferable, revocable license to use the app on
          devices you own or control, solely for your personal, non-commercial
          use, in accordance with the Apple App Store Terms of Service (where
          applicable) and these Terms.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          2. Age Requirement
        </h3>
        <p className="mb-4">
          You must be at least 18 years of age to use the Services. If you are
          not at least 18 years old, you may not access or use the Services.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          3. Auto-Renewable Subscriptions
        </h3>
        <p className="mb-4">
          The app may offer auto-renewable subscriptions under the subscription
          group <strong>IAATO Membership</strong>, including:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>
            <strong>Monthly Subscription</strong> — billed every 1 month
          </li>
          <li>
            <strong>Annually Subscription</strong> — billed every 1 year
          </li>
        </ul>
        <p className="mb-4">
          Current pricing is displayed in the app and on the App Store product
          page at the time of purchase and may vary by region.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          4. Payment &amp; Renewal (Apple App Store)
        </h3>
        <ul className="list-disc list-inside mb-4 space-y-2">
          <li>
            Payment will be charged to your Apple ID account at confirmation of
            purchase.
          </li>
          <li>
            Subscriptions automatically renew unless auto-renew is turned off at
            least 24 hours before the end of the current period.
          </li>
          <li>
            Your account will be charged for renewal within 24 hours prior to
            the end of the current period at the rate of the selected plan.
          </li>
          <li>
            You can manage or cancel your subscription in your Apple ID Account
            Settings after purchase. On iOS: Settings → [Your Name] →
            Subscriptions.
          </li>
          <li>
            Any unused portion of a free trial period, if offered, will be
            forfeited when you purchase a subscription to that publication,
            where applicable.
          </li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          5. Cancellation &amp; Refunds
        </h3>
        <p className="mb-4">
          You may cancel an App Store subscription at any time through your
          Apple ID subscription settings. Cancellation takes effect at the end
          of the current billing period. Refunds for App Store purchases are
          handled by Apple in accordance with Apple&apos;s refund policies.
          For non–App Store purchases (e.g., website billing), see our{" "}
          <Link to="/terms" className="text-indigo-600 hover:underline">
            Terms of Use (EULA)
          </Link>
          .
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          6. Educational Disclaimer
        </h3>
        <p className="mb-4">
          The Services are for general informational and educational purposes
          only. None of the Services is intended as investment, tax, accounting,
          or legal advice, as an offer or solicitation to buy or sell securities,
          or as an endorsement of any company, security, or fund. You are solely
          responsible for your own investment research and decisions.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          7. Restrictions
        </h3>
        <p className="mb-2">You agree not to:</p>
        <ul className="list-disc list-inside mb-4">
          <li>Copy, modify, distribute, or reverse engineer the app</li>
          <li>Share your account credentials with others</li>
          <li>
            Use the Services for any unlawful purpose or in violation of these
            Terms
          </li>
          <li>
            Attempt to interfere with the security or proper working of the
            Services
          </li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          8. Intellectual Property
        </h3>
        <p className="mb-4">
          All content, trademarks, logos, and materials in the Services are owned
          by It&apos;s All About The Options or its licensors and are protected
          by intellectual property laws. No rights are granted except as
          expressly set forth in this EULA.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          9. Termination
        </h3>
        <p className="mb-4">
          We may suspend or terminate your access to the Services if you violate
          this EULA or for any other reason at our discretion. Upon termination,
          the license granted herein ends immediately, and you must stop using
          the app.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          10. Limitation of Liability
        </h3>
        <p className="mb-4">
          To the maximum extent permitted by law, It&apos;s All About The Options
          and its affiliates shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages, or any loss of profits or
          data, arising from your use of the Services.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          11. Changes to These Terms
        </h3>
        <p className="mb-4">
          We may update this EULA from time to time. Material changes will be
          reflected on this page with an updated &quot;Last updated&quot; date.
          Continued use of the Services after changes constitutes acceptance of
          the revised Terms.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          12. Additional Terms
        </h3>
        <p className="mb-4">
          The full Terms of Use (EULA) and website membership terms are available
          at our{" "}
          <Link to="/terms" className="text-indigo-600 hover:underline">
            Terms of Use (EULA)
          </Link>{" "}
          page. If there is a conflict regarding App Store purchases,
          Apple&apos;s App Store rules and the EULA control for those purchases.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">13. Contact Us</h3>
        <p className="mb-2">
          If you have any questions about this Privacy Policy or Terms of Use
          (EULA), please contact us at:
        </p>
        <ul className="list-none mb-4 space-y-1">
          <li>
            Email:{" "}
            <a
              href="mailto:contact@itsallaboutheoptions.com"
              className="text-indigo-600 hover:underline"
            >
              contact@itsallaboutheoptions.com
            </a>
          </li>
          <li>
            Email:{" "}
            <a
              href="mailto:info@itsallaboutheoptions.com"
              className="text-indigo-600 hover:underline"
            >
              info@itsallaboutheoptions.com
            </a>
          </li>
          <li>
            <Link to="/#contact" className="font-medium text-indigo-600 hover:underline">
              Contact form
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
